import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import SEO from '../components/SEO';
import GalleryLightbox from '../components/GalleryLightbox';
import TrustpilotIcon from '../components/TrustpilotIcon';
import { COMPANY_INFO as STATIC_COMPANY_INFO, SERVICES as STATIC_SERVICES } from '../data/siteData';
import { useCompanyInfo, useServices } from '../lib/cms';

export default function HomePage() {
  const { companyInfo: COMPANY_INFO } = useCompanyInfo();
  const { services: SERVICES } = useServices();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveIndex] = useState(0);
  const [loadFb, setLoadFb] = useState(false);
  const fbContainerRef = useRef(null);

  useEffect(() => {
    const el = fbContainerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setLoadFb(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Video & Images from christiansmaskinservice.dk
  const heroVideo = "https://assets.auxodev.dk/assets/dde638d5-f83c-4036-8d58-4f922ae20635/min-film-3-1.mp4";
  const heroImage = "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/dde638d5-f83c-4036-8d58-4f922ae20635/265959446182484_4.jpg";

  const socialImages = [
    {
      url: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/dde638d5-f83c-4036-8d58-4f922ae20635/230080803103682_11.webp",
      caption: "Belægningsarbejde og graveopgave"
    },
    {
      url: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/dde638d5-f83c-4036-8d58-4f922ae20635/203665715745191_5.webp",
      caption: "Have- og anlægsarbejde i Vendsyssel"
    },
    {
      url: "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/dde638d5-f83c-4036-8d58-4f922ae20635/265959446182484_5.webp",
      caption: "Vognmandskørsel og entreprenørmaskiner"
    }
  ];

  const openLightbox = (idx) => {
    setActiveIndex(idx);
    setLightboxOpen(true);
  };

  // De 4 udvalgte primære ydelser på forsiden (Entreprenørarbejde, Haveservice, Vognmandskørsel, Nedbrydning)
  const FEATURED_SERVICE_SLUGS = ['entreprenoer', 'belaegning-have', 'vognmand', 'nedbrydning'];
  const featuredServices = FEATURED_SERVICE_SLUGS
    .map((slug) => SERVICES.find((s) => s.slug === slug))
    .filter(Boolean);

  return (
    <>
      <SEO
        title="CMSNord.dk - Din entreprenør i Brønderslev"
        description="CMSNord.dk tilbyder entreprenørarbejde, belægning & have, vognmandskørsel og nedbrydning i Brønderslev og omegn. Huller & haver - er noget af det vi laver."
        canonicalUrl={window.location.origin}
        ogImage={heroImage}
      />

      {/* 1. HERO SECTION - ENKEL & DIREKTE MED VIDEO */}
      <section className="relative w-full bg-slate-950 text-white py-16 sm:py-24 overflow-hidden border-b border-slate-800">
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            poster={heroImage}
            className="w-full h-full object-cover opacity-80"
          >
            <source src={heroVideo} type="video/mp4" />
            <img
              src={heroImage}
              alt="CMSNord.dk - Entreprenørarbejde og maskiner"
              className="w-full h-full object-cover"
            />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/70 via-slate-950/40 to-slate-950/15" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-tight uppercase">
              Din entreprenør <br className="hidden sm:inline" />
              <span className="text-[#F4AF00]">i Brønderslev</span>
            </h1>

            <p className="text-xl sm:text-2xl font-bold text-slate-200 uppercase tracking-wide border-l-4 border-[#F4AF00] pl-4 py-1">
              Huller &amp; haver – er noget af det vi laver
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-4">
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="px-8 py-4 bg-[#F4AF00] hover:bg-[#D99B00] text-[#121212] font-black text-sm sm:text-base uppercase tracking-wider transition-all shadow-xl hover:scale-105 flex items-center gap-2 cursor-pointer"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span>Ring: {COMPANY_INFO.phone}</span>
              </a>

              <NavLink
                to="/kontakt"
                className="px-8 py-4 bg-white hover:bg-zinc-100 text-[#121212] font-black text-sm sm:text-base uppercase tracking-wider transition-all hover:scale-105 flex items-center gap-2 shadow-lg cursor-pointer"
              >
                <span>Send besked</span>
                <i className="fa-solid fa-arrow-right text-xs text-[#F4AF00]" aria-hidden="true" />
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      {/* 2. YDELSER SECTION - 4 OVERSIKTLIGE KORT */}
      <section className="w-full bg-[#F4F4F6] py-16 sm:py-24 px-6 sm:px-12 border-b border-zinc-300">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-[#121212] uppercase tracking-tight">
              Vores ydelser
            </h2>
            <div className="w-16 h-1 bg-[#F4AF00] mx-auto mt-4" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 w-full">
            {featuredServices.map((service) => (
              <NavLink
                key={service.slug}
                to={`/ydelser/${service.slug}`}
                className="group relative h-64 sm:h-72 md:h-80 overflow-hidden border-2 border-zinc-300 hover:border-[#F4AF00] shadow-lg hover:shadow-2xl transition-all duration-300 flex items-end p-6 sm:p-8 hover:-translate-y-1.5 cursor-pointer block"
              >
                {/* Background Image */}
                <img
                  src={service.primaryImage || service.image}
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />

                {/* Dark Gradient Overlay for optimal legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212]/90 via-[#121212]/50 to-[#121212]/20 group-hover:from-[#121212]/95 group-hover:via-[#121212]/60 transition-colors duration-300" />

                {/* Title Overlay */}
                <div className="relative z-10 w-full flex items-center justify-between gap-4">
                  <div>
                    <span className="inline-block w-8 h-1 bg-[#F4AF00] mb-2 group-hover:w-12 transition-all duration-300" />
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-white uppercase tracking-wide drop-shadow-md group-hover:text-[#F4AF00] transition-colors">
                      {service.title}
                    </h3>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-xs border border-white/20 flex items-center justify-center text-white group-hover:bg-[#F4AF00] group-hover:text-[#121212] group-hover:border-[#F4AF00] transition-all duration-300 shrink-0">
                    <i className="fa-solid fa-arrow-right text-sm group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                  </div>
                </div>
              </NavLink>
            ))}
          </div>

          {/* Se alle ydelser knap */}
          <div className="mt-12 sm:mt-14 text-center">
            <NavLink
              to="/ydelser"
              className="inline-flex items-center justify-center gap-3 px-8 sm:px-10 py-4 sm:py-5 bg-[#121212] hover:bg-[#F4AF00] text-white hover:text-[#121212] font-black text-base sm:text-lg uppercase tracking-wider transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 border border-zinc-700 hover:border-[#F4AF00] cursor-pointer group"
            >
              <span>Se alle ydelser</span>
              <i className="fa-solid fa-arrow-right text-sm text-[#F4AF00] group-hover:text-[#121212] group-hover:translate-x-1 transition-all" aria-hidden="true" />
            </NavLink>
          </div>
        </div>
      </section>

      {/* 3. KONTAKT OS I DAG - REN & ENKEL SEKTION */}
      <section className="w-full bg-white py-16 sm:py-20 px-6 sm:px-12 border-b border-zinc-200">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-black text-[#121212] uppercase tracking-tight">
            Kontakt os i dag
          </h2>

          <p className="text-zinc-700 text-base sm:text-lg leading-relaxed font-normal max-w-3xl mx-auto">
            Vi står klar til at hjælpe dig. Uanset om du har brug for hjælp til dit haveprojekt, større anlægsopgaver, vognmandskørsel eller nedbrydning, så er vi din entreprenør. Vi er klar til at lytte til dine idéer, og kan forvandle dine planer til virkelighed.
          </p>

          <div className="pt-4 flex flex-wrap justify-center items-center gap-6">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="px-8 py-4 bg-[#121212] hover:bg-zinc-800 text-white font-black text-base uppercase tracking-wider transition-all shadow-xl hover:scale-105 flex items-center gap-3"
            >
              <i className="fa-solid fa-phone text-[#F4AF00] text-lg" aria-hidden="true" />
              <span>Telefon: {COMPANY_INFO.phone}</span>
            </a>

            <NavLink
              to="/kontakt"
              className="px-8 py-4 bg-[#F4AF00] hover:bg-[#D99B00] text-[#121212] font-black text-base uppercase tracking-wider transition-all shadow-xl hover:scale-105 flex items-center gap-2"
            >
              <span>Gå til kontaktside</span>
              <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
            </NavLink>
          </div>
        </div>
      </section>

      {/* 4. FORENET SEKTION: SOCIALE MEDIER & KUNDEANMELDELSER */}
      <section className="w-full bg-[#111318] text-white pt-12 sm:pt-16 pb-12 sm:pb-16 px-4 sm:px-8 lg:px-12 border-t border-zinc-800 overflow-hidden relative">
        {/* Subtle Background Glows */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#F4AF00]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-10 w-96 h-96 bg-[#00B67A]/5 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* VENSTRE SIDE: FACEBOOK PAGE PLUGIN + SENESTE OPDATERING */}
            <div className="lg:col-span-5 flex justify-center items-center order-2 lg:order-1">
              <div className="relative mx-auto w-full max-w-[340px] sm:max-w-[370px]">
                {/* iPhone Ydre Ramme med metalkant og skygge */}
                <div className="relative rounded-[48px] sm:rounded-[54px] p-3 sm:p-3.5 bg-black shadow-[0_25px_60px_-15px_rgba(0,0,0,0.95)] ring-1 ring-zinc-900">
                  
                  {/* Fysiske knapper på rammen */}
                  <div className="absolute -left-[3px] top-24 w-[3px] h-7 bg-zinc-900 rounded-l-sm" />
                  <div className="absolute -left-[3px] top-36 w-[3px] h-12 bg-zinc-900 rounded-l-sm" />
                  <div className="absolute -left-[3px] top-52 w-[3px] h-12 bg-zinc-900 rounded-l-sm" />
                  <div className="absolute -right-[3px] top-32 w-[3px] h-16 bg-zinc-900 rounded-r-sm" />

                  {/* Skærm-omrids */}
                  <div className="relative bg-white rounded-[38px] sm:rounded-[44px] overflow-hidden border border-black shadow-inner flex flex-col">

                    {/* Skærmens indhold (Facebook Page feed tilpasset iPhone-skærmen) */}
                    <div ref={fbContainerRef} className="w-full h-[580px] sm:h-[620px] bg-white overflow-hidden flex justify-center">
                      {loadFb ? (
                        <iframe
                          title="Christians Maskinservice på Facebook"
                          src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2FChristiansMaskinService%2F&show_posts=true&width=340&height=620&adapt_container_width=true&small_header=false"
                          width="340"
                          height="620"
                          className="border-0 w-full h-full"
                          style={{ border: 0, overflow: 'hidden', display: 'block', maxWidth: '100%' }}
                          scrolling="no"
                          loading="lazy"
                          allow="encrypted-media;picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400 gap-2 p-4">
                          <i className="fa-brands fa-facebook text-3xl text-[#1877F2]" aria-hidden="true" />
                          <span className="text-xs font-medium">Henter Facebook...</span>
                        </div>
                      )}
                    </div>

                    {/* iPhone Home Indicator i bunden */}
                    <div className="h-6 bg-white flex items-center justify-center select-none shrink-0">
                      <div className="w-32 h-1 bg-zinc-800 rounded-full" />
                    </div>

                  </div>
                </div>
              </div>
            </div>

            {/* HØJRE SIDE: OVERSKRIFT, ANMELDELSER OG HANDLINGS-KNAPPER */}
            <div className="lg:col-span-7 space-y-4 sm:space-y-5 text-left order-1 lg:order-2">
              <div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zinc-900 border border-zinc-800 mb-2.5 shadow-sm">
                  <span className="flex text-[#F4AF00] text-[11px] gap-0.5">
                    <i className="fa-solid fa-star" aria-hidden="true" />
                    <i className="fa-solid fa-star" aria-hidden="true" />
                    <i className="fa-solid fa-star" aria-hidden="true" />
                    <i className="fa-solid fa-star" aria-hidden="true" />
                    <i className="fa-solid fa-star" aria-hidden="true" />
                  </span>
                  <span className="text-[11px] font-bold text-zinc-300 uppercase tracking-wider">
                    Følg med &amp; kundeanmeldelser
                  </span>
                </div>

                <h2 className="text-2xl sm:text-4xl font-black text-white uppercase tracking-tight leading-tight">
                  Billeder fra hverdagen &amp; Vores kunders vurdering
                </h2>
                <div className="w-14 h-1 bg-[#F4AF00] mt-2.5" />
              </div>

              <p className="text-zinc-300 text-xs sm:text-sm md:text-base leading-relaxed">
                Få et indblik i vores hverdag med opdateringer fra igangværende projekter i Nordjylland – og se hvad vores kunder siger om vores arbejde.
              </p>

              {/* 2 KOMPAKTE ANMELDELSESKORT SIDE-BY-SIDE */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                {/* TRUSTPILOT KORT */}
                <div className="bg-zinc-900/90 border border-zinc-800 hover:border-[#00B67A] transition-all duration-200 rounded-xl p-3.5 flex flex-col justify-between shadow-md group">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-md bg-[#00B67A]/15 border border-[#00B67A]/30 flex items-center justify-center shrink-0">
                          <TrustpilotIcon className="w-3.5 h-3.5" />
                        </div>
                        <span className="text-sm font-bold text-white group-hover:text-[#00B67A] transition-colors">
                          Trustpilot
                        </span>
                      </div>
                      <div className="flex items-center gap-0.5 bg-[#00B67A] px-1.5 py-0.5 rounded text-white text-[9px] font-bold">
                        <span>★★★★★</span>
                      </div>
                    </div>
                    <p className="text-zinc-400 text-xs leading-relaxed">
                      Læs verificerede kundeanmeldelser eller del din oplevelse.
                    </p>
                  </div>
                  <div className="mt-2.5 pt-2 border-t border-zinc-800 flex items-center justify-between">
                    <a
                      href={COMPANY_INFO.trustpilotReviews || "https://dk.trustpilot.com/review/christiansmaskinservice.dk"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-bold text-[#00B67A] hover:underline"
                    >
                      <span>Læs anmeldelser</span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-[9px]" aria-hidden="true" />
                    </a>
                  </div>
                </div>

                {/* FACEBOOK ANMELDELSER KORT */}
                <div className="bg-zinc-900/90 border border-zinc-800 hover:border-[#1877F2] transition-all duration-200 rounded-xl p-3.5 flex flex-col justify-between shadow-md group">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-md bg-[#1877F2]/15 border border-[#1877F2]/30 flex items-center justify-center text-[#1877F2] text-xs shrink-0">
                          <i className="fa-brands fa-facebook-f" aria-hidden="true" />
                        </div>
                        <span className="text-sm font-bold text-white group-hover:text-[#1877F2] transition-colors">
                          Facebook
                        </span>
                      </div>
                      <div className="flex items-center gap-0.5 bg-[#1877F2] px-1.5 py-0.5 rounded text-white text-[9px] font-bold">
                        <span>★★★★★</span>
                      </div>
                    </div>
                    <p className="text-zinc-400 text-xs leading-relaxed">
                      Se lokale anbefalinger og følg med i vores arbejde.
                    </p>
                  </div>
                  <div className="mt-2.5 pt-2 border-t border-zinc-800 flex items-center justify-between">
                    <a
                      href={COMPANY_INFO.facebookReviews || "https://www.facebook.com/ChristiansMaskinService/reviews?locale=da_DK"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-bold text-[#1877F2] hover:underline"
                    >
                      <span>Læs anmeldelser</span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-[9px]" aria-hidden="true" />
                    </a>
                  </div>
                </div>
              </div>

              {/* HANDLINGS-KNAPPER */}
              <div className="pt-2 flex flex-wrap items-center gap-2.5 sm:gap-3">
                <a
                  href={COMPANY_INFO.facebookPage || "https://www.facebook.com/ChristiansMaskinService/"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#1877F2] hover:bg-[#166fe5] text-white font-bold text-xs sm:text-sm uppercase tracking-wider transition-all shadow-lg hover:scale-105 rounded-lg"
                >
                  <i className="fa-brands fa-facebook text-base" aria-hidden="true" />
                  <span>Følg os på Facebook</span>
                </a>

                <NavLink
                  to="/galleri"
                  className="inline-flex items-center gap-1.5 px-5 py-3 bg-[#F4AF00] hover:bg-[#D99B00] text-[#121212] font-black text-xs sm:text-sm uppercase tracking-wider transition-all shadow-lg hover:scale-105 rounded-lg"
                >
                  <i className="fa-solid fa-images text-xs" aria-hidden="true" />
                  <span>Se vores galleri</span>
                </NavLink>

                <a
                  href={COMPANY_INFO.trustpilotReviews || "https://dk.trustpilot.com/review/christiansmaskinservice.dk"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3.5 py-3 bg-zinc-900 hover:bg-[#00B67A] text-zinc-200 hover:text-white border border-zinc-700 hover:border-[#00B67A] font-semibold text-xs transition-all rounded-lg group/tp"
                >
                  <TrustpilotIcon className="w-3.5 h-3.5 group-hover/tp:brightness-200 transition-all" />
                  <span>Del din mening</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Lightbox for visual inspection */}
      <GalleryLightbox
        images={socialImages}
        isOpen={lightboxOpen}
        initialIndex={activeImageIndex}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}
